import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../queryKeys';
import {
  inventoryApi,
  QueryInventoryParams,
  CreateInventoryItemDto,
  RestockDto,
  TransferDto,
} from '../services/inventory.api';
import { toast } from 'sonner';
import { getErrorMessage } from '../client';

/**
 * Query hook for fetching paginated inventory list
 */
export function useInventoryList(params: QueryInventoryParams = {}) {
  return useQuery({
    queryKey: queryKeys.inventory.list(params),
    queryFn: () => inventoryApi.getAll(params),
  });
}

/**
 * Query hook for fetching a single inventory item
 */
export function useInventoryItem(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.inventory.detail(id || ''),
    queryFn: () => inventoryApi.getById(id!),
    enabled: !!id,
  });
}

/**
 * Query hook for fetching inventory levels for an item
 */
export function useInventoryLevels(itemId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.inventory.levels(itemId || ''),
    queryFn: () => inventoryApi.getLevels(itemId!),
    enabled: !!itemId,
  });
}

/**
 * Query hook for fetching inventory dashboard data
 */
export function useInventoryDashboard(companyId?: string) {
  return useQuery({
    queryKey: [...queryKeys.inventory.all, 'dashboard', companyId],
    queryFn: () => inventoryApi.getDashboard(companyId),
  });
}

/**
 * Mutation hook for creating a new inventory item
 */
export function useCreateInventoryItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateInventoryItemDto) => inventoryApi.create(data),
    onSuccess: () => {
      toast.success('Inventory item created successfully');
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.lists() });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Mutation hook for updating an inventory item
 */
export function useUpdateInventoryItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateInventoryItemDto> }) =>
      inventoryApi.update(id, data),
    onSuccess: (_, variables) => {
      toast.success('Inventory item updated successfully');
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.detail(variables.id) });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Mutation hook for deleting an inventory item
 */
export function useDeleteInventoryItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => inventoryApi.delete(id),
    onSuccess: () => {
      toast.success('Inventory item deleted successfully');
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.lists() });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Mutation hook for restocking inventory
 */
export function useRestockInventory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: RestockDto) => inventoryApi.restock(data),
    onSuccess: (_, variables) => {
      toast.success('Restock initiated successfully');
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.detail(variables.itemId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.levels(variables.itemId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.shipments.lists() });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Mutation hook for transferring inventory
 */
export function useTransferInventory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: TransferDto) => inventoryApi.transfer(data),
    onSuccess: () => {
      toast.success('Transfer initiated successfully');
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.shipments.lists() });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Mutation hook for adding inventory level at a location
 */
export function useAddInventoryLevel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      itemId,
      data,
    }: {
      itemId: string;
      data: { locationId: string; quantity?: number };
    }) => inventoryApi.addLevel(itemId, data),
    onSuccess: (_, variables) => {
      toast.success('Inventory level added successfully');
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.levels(variables.itemId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.detail(variables.itemId) });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Mutation hook for updating inventory level at a location
 */
export function useUpdateInventoryLevel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      itemId,
      locationId,
      data,
    }: {
      itemId: string;
      locationId: string;
      data: { quantity: number; reservedQty?: number };
    }) => inventoryApi.updateLevel(itemId, locationId, data),
    onSuccess: (_, variables) => {
      toast.success('Inventory level updated successfully');
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.levels(variables.itemId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.detail(variables.itemId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.lists() });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}
